/**
 * Staff session on the client: one react-query entry (`admin.auth.session`), filled by the sign-in pages,
 * read by the route guards (route-base.tsx) and the UI helpers below.
 */
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { ApiError, call } from "@/api/client";
import { ADMIN_ONLY_HINT, FORBIDDEN_MESSAGE, levelOf, ROLE_SCOPE, roleCan, type Capability, type Level } from "@/contracts/permissions";
import { staffAuthApi, type StaffSession } from "@/contracts/staff-auth";

export const sessionQuery = queryOptions({
  queryKey: [staffAuthApi.session.id],
  queryFn: async (): Promise<StaffSession | null> => {
    try {
      return await call(staffAuthApi.session);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  staleTime: 5 * 60_000,
});

/** The signed-in staff member (inside the shell: always set). */
export function useSession(): StaffSession {
  // Filled by the staff route's beforeLoad before any screen renders.
  const s = useSuspenseQuery(sessionQuery).data;
  if (!s) throw new ApiError(401, "session_expired", "Session expirée. Merci de vous reconnecter.");
  return s;
}

export function useSetSession() {
  const qc = useQueryClient();
  return (s: StaffSession | null) => qc.setQueryData(sessionQuery.queryKey, s);
}

/** `can("finance", "write")` for the signed-in role. */
export function useCan() {
  const { user } = useSession();
  return (cap: Capability, mode: "read" | "write" = "read") => roleCan(user.role, cap, mode);
}

export function useLevel(cap: Capability): Level {
  return levelOf(useSession().user.role, cap);
}

/** Read-only roles see the permanent banner (BO drawer, AG-Lecteur). */
export function useReadOnly(cap: Capability): boolean {
  return useLevel(cap) === "read";
}

/** COMPTABLE works on attached companies only: label the perimeter (« Périmètre : 4 entreprises »). */
export function useScope() {
  const s = useSession();
  return { scope: ROLE_SCOPE[s.user.role], companyIds: s.attachedCompanyIds };
}

/**
 * Props for an action the role may lack (AG-Matrice: greyed, `aria-disabled`, tooltip « Réservé à l'administrateur »).
 *   <Button {...gate("creation.adminActions")}>Révéler le NIR</Button>
 * Hidden instead of disabled where the board says so (LECTEUR's quick-action button): use `<Can>`.
 */
export function useGate() {
  const can = useCan();
  return (cap: Capability, mode: "read" | "write" = "write", hint: string = cap.endsWith("adminActions") || cap === "team" ? ADMIN_ONLY_HINT : FORBIDDEN_MESSAGE) =>
    can(cap, mode) ? {} : ({ disabled: true, "aria-disabled": true, title: hint } as const);
}
